import { UserId } from '@cibo/core'
import { AuthUserPermission, Can, Loading, useOrgUser, usePermissions } from '@cibo/profile'
import { useFigures, useParamAsInteger } from '@cibo/ui'
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded'
import DownloadRounded from '@mui/icons-material/DownloadRounded'
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { CreateErrorReportDialog } from 'features/Agreements'
import { ADMIN_ROUTES, ROUTE } from 'navigation/types'
import { ACCOUNT_ROUTES } from 'navigation/types/routes'
import { groupBy } from 'ramda'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import {
  AgreementModel,
  AgreementStatusChip,
  useAgreements,
  useDownloadAgreement,
} from '../../../features/Agreements'
import { AgreementDetail } from '../../Agreements'
import { UserOfferListItem } from './UserOfferListItem'

export const UserAgreements = ({
  currentUser,
  userId,
}: {
  currentUser?: boolean
  userId?: UserId
}) => {
  const routeUserId = useParamAsInteger('userId')

  const user = useOrgUser(routeUserId)
  const { t } = useTranslation('impact/pages/UserAgreements')
  const { modifiedDate } = useFigures()
  const { can } = usePermissions()
  const navigate = useNavigate()
  const { openFile } = useDownloadAgreement()
  const agreements = useAgreements({ userId: userId || routeUserId })

  const [showDialog, setShowDialog] = useState(false)

  const agreementsByYear: Record<string, AgreementModel[]> = useMemo(
    () =>
      agreements.data ? groupBy(a => `${a.modifiedDate.getFullYear()}`, agreements.data.items) : {},
    [agreements.dataUpdatedAt]
  )

  const agreementYears = Object.keys(agreementsByYear)
    .sort()
    .reverse()

  if (
    !can({
      useAny: [
        AuthUserPermission.AGREEMENTS,
        AuthUserPermission.AGREEMENTS_OFFER_READ,
        AuthUserPermission.AGREEMENTS_OFFER_WRITE,
        AuthUserPermission.AGREEMENTS_OFFER_CREATE,
      ],
    })
  ) {
    navigate(ROUTE.HOME)
  }

  if (agreements.isFetching) {
    return <Loading />
  }

  if (agreements.data && agreements.data.numAvailable === 1) {
    return <AgreementDetail agreementId={agreements.data.items[0].agreementId} variant="user" />
  }

  const onClickAgreement = (agreementId: string) => () =>
    navigate(
      currentUser
        ? generatePath(ACCOUNT_ROUTES.AGREEMENT_DETAIL, {
            agreementId,
          })
        : generatePath(ADMIN_ROUTES.USER_AGREEMENT_DETAIL, {
            userId: routeUserId?.toString(),
            agreementId,
          })
    )

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h4">{t('agreements')}</Typography>

        {!!(user.data?.user?.persona === 'grower') && (
          <Box>
            <Can useAny={AuthUserPermission.AGREEMENTS_ADMIN_REVIEW_CREATE}>
              <Button
                onClick={() => setShowDialog(true)}
                variant="contained"
                size="small"
                startIcon={<AddCircleOutlineRounded />}
                data-testid="create-error-report-button"
              >
                {t('createErrorReport', { ns: 'impact/pages/Admin/AgreementsOverview' })}
              </Button>
            </Can>

            <CreateErrorReportDialog
              open={showDialog}
              onClose={() => setShowDialog(false)}
              userId={routeUserId}
            />
          </Box>
        )}
      </Stack>

      <Typography>{t('agreementsSubhead')}</Typography>

      {agreements.isFetching ? (
        <Skeleton />
      ) : !agreements.data || agreements.data.numAvailable === 0 ? (
        <Typography>{t('noAgreements')}</Typography>
      ) : (
        <>
          {agreementYears.map(year => {
            const agreementGroup = agreementsByYear[year]

            return (
              <Stack divider={<Divider />} key={year}>
                <Typography variant="h5">{year}</Typography>

                <List>
                  {agreementGroup.map(agreement =>
                    agreement.agreementType === 'offer' ? (
                      <Can useAny={AuthUserPermission.AGREEMENTS_OFFER_READ}>
                        <ListItem
                          key={agreement.agreementId}
                          secondaryAction={
                            !!agreement.docPath ? (
                              <IconButton
                                edge="end"
                                aria-label={t('download')}
                                onClick={openFile(agreement)}
                              >
                                <DownloadRounded color="secondary" />
                              </IconButton>
                            ) : (
                              undefined
                            )
                          }
                          disablePadding
                        >
                          <UserOfferListItem
                            agreement={agreement}
                            onClickAgreement={onClickAgreement}
                          />
                        </ListItem>
                      </Can>
                    ) : (
                      <ListItem
                        data-testid={`agreementListItem-${agreement.agreementId}`}
                        key={agreement.agreementId}
                        secondaryAction={
                          !!agreement.docPath ? (
                            <IconButton
                              edge="end"
                              aria-label={t('download')}
                              onClick={openFile(agreement)}
                            >
                              <DownloadRounded color="secondary" />
                            </IconButton>
                          ) : (
                            undefined
                          )
                        }
                        disablePadding
                      >
                        <ListItemButton
                          sx={{ paddingBlock: 1, height: '48px' }}
                          onClick={onClickAgreement(agreement.agreementId)}
                        >
                          <ListItemIcon sx={{ width: '90px' }}>
                            <AgreementStatusChip agreement={agreement} concise />
                          </ListItemIcon>
                          <ListItemText sx={{ marginInline: 2 }}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography color="secondary">
                                {t('agreementName', { context: agreement.agreementType })}
                              </Typography>
                              <Typography variant="caption" color="secondary">
                                {modifiedDate(agreement.modifiedDate)}
                              </Typography>
                            </Stack>
                          </ListItemText>
                        </ListItemButton>
                      </ListItem>
                    )
                  )}
                </List>
              </Stack>
            )
          })}
        </>
      )}
    </Stack>
  )
}
